import { format } from 'date-fns';
import { downloadExcelWorkbook } from './exportExcel';

const normalizeText = (value) => String(value ?? '').trim();
const toPositiveInteger = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric) : 0;
};

const slugPart = (value, fallback) => {
  const lettersOnly = normalizeText(value).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return (lettersOnly.slice(0, 3) || fallback).padEnd(3, 'X');
};

const compactCode = (value) => normalizeText(value).replace(/[^A-Za-z0-9]/g, '').toUpperCase();

const buildStoreTransferCode = ({ storeCode, branchCode, vendorId } = {}) => {
  const vendorKey = compactCode(vendorId);
  if (vendorKey) {
    return `OM-${vendorKey}`;
  }

  const normalizedStoreCode = compactCode(storeCode || branchCode);
  if (normalizedStoreCode) {
    return normalizedStoreCode.slice(0, 8);
  }

  return 'OM';
};

const parseStatus = (value) => {
  const normalized = normalizeText(value).toLowerCase();
  return normalized === 'completed' ? 'Completed' : 'Incomplete';
};

export const createReturnRow = () => ({
  id: `return-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  productInput: '',
  productId: '',
  productName: '',
  sku: '',
  quantity: '',
  reason: 'Damaged',
  manufacturingDate: '',
  expiryDate: '',
});

export const normalizeReturnEntry = (entry = {}) => {
  const items = Array.isArray(entry.items)
    ? entry.items.map((item, index) => ({
        id: normalizeText(item.id) || `item-${index + 1}`,
        productId: normalizeText(item.productId),
        productName: normalizeText(item.productName || item.product),
        sku: normalizeText(item.sku),
        quantity: toPositiveInteger(item.quantity),
        reason: normalizeText(item.reason) || 'Damaged',
        manufacturingDate: normalizeText(item.manufacturingDate),
        expiryDate: normalizeText(item.expiryDate),
      }))
    : [];

  const normalizedItems = items.filter((item) => item.productName || item.sku || item.quantity > 0);
  const totalQuantity = normalizedItems.reduce((sum, item) => sum + item.quantity, 0);

  return {
    id: normalizeText(entry.id) || `return-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    transferName: normalizeText(entry.transferName),
    storeId: normalizeText(entry.storeInfoId || entry.store_info_id || entry.storeId),
    storeInfoId: normalizeText(entry.storeInfoId || entry.store_info_id),
    legacyStoreId: normalizeText(entry.legacyStoreId || entry.legacy_store_id),
    vendorId: normalizeText(entry.vendorId || entry.vendor_id),
    storeName: normalizeText(entry.storeName),
    sellerName: normalizeText(entry.sellerName),
    status: parseStatus(entry.status),
    items: normalizedItems,
    skuCount: normalizedItems.length,
    totalQuantity,
    timestamp: normalizeText(entry.timestamp) || new Date().toISOString(),
    createdAt: normalizeText(entry.createdAt) || normalizeText(entry.timestamp) || new Date().toISOString(),
    updatedAt: normalizeText(entry.updatedAt) || normalizeText(entry.timestamp) || new Date().toISOString(),
    createdBy: normalizeText(entry.createdBy),
    updatedBy: normalizeText(entry.updatedBy),
    source: normalizeText(entry.source) || 'darkstore_product_return_entries',
    editControl: {
      confirmedLocked: Boolean(entry.editControl?.confirmedLocked ?? entry.edit_locked ?? false),
      unlockRequested: Boolean(entry.editControl?.unlockRequested ?? entry.unlock_requested ?? false),
      unlockRequestedAt: entry.editControl?.unlockRequestedAt || entry.unlock_requested_at || null,
      unlockRequestedById: entry.editControl?.unlockRequestedById || entry.unlock_requested_by || null,
      unlockRequestedByName: entry.editControl?.unlockRequestedByName || entry.unlock_requested_by_name || '',
      unlockedAt: entry.editControl?.unlockedAt || entry.unlocked_at || null,
      unlockedById: entry.editControl?.unlockedById || entry.unlocked_by || null,
      unlockedByName: entry.editControl?.unlockedByName || entry.unlocked_by_name || '',
    },
  };
};

const entryTimestamp = (entry = {}) => {
  const timestamp = new Date(entry.updatedAt || entry.timestamp || entry.createdAt || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const toTransferDisplayKey = (entry = {}) => {
  const transferName = normalizeText(entry.transferName).toLowerCase();
  const scopedStore = normalizeText(entry.storeInfoId || entry.storeId || entry.storeName).toLowerCase();
  if (!transferName) {
    return `entry:${normalizeText(entry.id)}`;
  }
  return `${scopedStore}::${transferName}`;
};

export const formatProductNamesSummary = (entry = {}, maxNames = 3) => {
  const names = (entry.items || [])
    .map((item) => normalizeText(item.productName))
    .filter(Boolean);
  if (!names.length) return '-';
  const preview = names.slice(0, maxNames).join(', ');
  return names.length > maxNames ? `${preview}, ...` : preview;
};

export const collapseProductReturnLogsForDisplay = (entries = []) => {
  const groupedEntries = new Map();

  (entries || []).forEach((entry) => {
    const normalizedEntry = normalizeReturnEntry(entry);
    const key = toTransferDisplayKey(normalizedEntry);
    if (!groupedEntries.has(key)) {
      groupedEntries.set(key, []);
    }
    groupedEntries.get(key).push(normalizedEntry);
  });

  const collapsed = [];
  groupedEntries.forEach((group) => {
    const sortedGroup = [...group].sort((left, right) => entryTimestamp(right) - entryTimestamp(left));
    const latestCompleted = sortedGroup.find((entry) => parseStatus(entry.status) === 'Completed');
    collapsed.push(latestCompleted || sortedGroup[0]);
  });

  return collapsed.sort((left, right) => entryTimestamp(right) - entryTimestamp(left));
};

export const buildTransferName = ({ storeName, storeCode, branchCode, vendorId, sellerName, entries, currentEntry }) => {
  const storePart = buildStoreTransferCode({ storeCode, branchCode, vendorId });
  const sellerPart = slugPart(sellerName, 'SEL');
  const prefix = `${storePart}-${sellerPart}`;

  const currentTransfer = normalizeText(currentEntry?.transferName);
  if (currentTransfer) {
    return currentTransfer;
  }

  const nextSequence = entries.reduce((maxValue, entry) => {
    if (!normalizeText(entry.transferName).startsWith(`${prefix}-`)) return maxValue;
    const sequence = Number(normalizeText(entry.transferName).split('-').pop());
    return Number.isFinite(sequence) ? Math.max(maxValue, sequence) : maxValue;
  }, 0) + 1;

  return `${prefix}-${nextSequence}`;
};

export const exportProductReturnEntry = (entry) => {
  const normalizedEntry = normalizeReturnEntry(entry);

  const summaryRows = [
    { Field: 'Transfer Name', Value: normalizedEntry.transferName },
    { Field: 'Store Name', Value: normalizedEntry.storeName },
    { Field: 'Seller', Value: normalizedEntry.sellerName },
    { Field: 'Status', Value: normalizedEntry.status },
    { Field: 'SKU Count', Value: normalizedEntry.skuCount },
    { Field: 'Total Quantity', Value: normalizedEntry.totalQuantity },
    { Field: 'Timestamp', Value: normalizedEntry.timestamp ? format(new Date(normalizedEntry.timestamp), 'PPpp') : '-' },
    { Field: 'Created By', Value: normalizedEntry.createdBy || '-' },
    { Field: 'Updated By', Value: normalizedEntry.updatedBy || '-' },
  ];

  const productRows = normalizedEntry.items.map((item) => ({
    Product: item.productName,
    SKU: item.sku || '-',
    Quantity: item.quantity,
    Reason: item.reason,
    'Manufacturing Date': item.manufacturingDate || '-',
    'Expiry Date': item.expiryDate || '-',
  }));

  downloadExcelWorkbook({
    fileName: `${normalizedEntry.transferName || 'product-return'}.xlsx`,
    sheets: [
      {
        sheetName: 'Summary',
        columns: [
          { header: 'Field', key: 'Field', width: 24 },
          { header: 'Value', key: 'Value', width: 40 },
        ],
        rows: summaryRows,
      },
      {
        sheetName: 'Products',
        columns: [
          { header: 'Product', key: 'Product', width: 32 },
          { header: 'SKU', key: 'SKU', width: 18 },
          { header: 'Quantity', key: 'Quantity', width: 12 },
          { header: 'Reason', key: 'Reason', width: 18 },
          { header: 'Manufacturing Date', key: 'Manufacturing Date', width: 18 },
          { header: 'Expiry Date', key: 'Expiry Date', width: 18 },
        ],
        rows: productRows.length ? productRows : [{ Product: 'No items', SKU: '-', Quantity: 0, Reason: '-', 'Manufacturing Date': '-', 'Expiry Date': '-' }],
      },
    ],
  });
};

export const exportProductReturnReport = (entries = [], filename = 'product-returns-report.xlsx') => {
  const normalizedEntries = entries.map(normalizeReturnEntry);

  const reportRows = normalizedEntries.flatMap((entry) => (
    entry.items.map((item) => ({
      'Transfer Name': entry.transferName,
      Store: entry.storeName,
      Seller: entry.sellerName,
      Status: entry.status,
      Product: item.productName,
      SKU: item.sku || '-',
      Quantity: item.quantity,
      Reason: item.reason,
      'Manufacturing Date': item.manufacturingDate || '-',
      'Expiry Date': item.expiryDate || '-',
      'SKU Count': entry.skuCount,
      'Entry Total Quantity': entry.totalQuantity,
      Timestamp: entry.timestamp ? format(new Date(entry.timestamp), 'PPpp') : '-',
    }))
  ));

  downloadExcelWorkbook({
    fileName: filename,
    sheetName: 'Returns Report',
    columns: [
      { header: 'Transfer Name', key: 'Transfer Name', width: 24 },
      { header: 'Store', key: 'Store', width: 20 },
      { header: 'Seller', key: 'Seller', width: 20 },
      { header: 'Status', key: 'Status', width: 14 },
      { header: 'Product', key: 'Product', width: 28 },
      { header: 'SKU', key: 'SKU', width: 18 },
      { header: 'Quantity', key: 'Quantity', width: 12 },
      { header: 'Reason', key: 'Reason', width: 18 },
      { header: 'Manufacturing Date', key: 'Manufacturing Date', width: 18 },
      { header: 'Expiry Date', key: 'Expiry Date', width: 18 },
      { header: 'SKU Count', key: 'SKU Count', width: 12 },
      { header: 'Entry Total Quantity', key: 'Entry Total Quantity', width: 18 },
      { header: 'Timestamp', key: 'Timestamp', width: 22 },
    ],
    rows: reportRows.length ? reportRows : [{ 'Transfer Name': 'No records found' }],
  });
};
